import './ProductList.scss';

import { getProductListHtmlByAjax } from '@apis/ajax';
import type { RegistryComponentProps } from '@boost-sd/components-registry';
import { registryComponent } from '@boost-sd/components-registry/registry';
import { getQueryParamByKey, setQueryParam } from '@boost-sd/core-js/history';
import PartialRender from '@components/PartialRender';
import {
  CURRENT_PER_TOTAL,
  DEFAULT,
  INFINITE,
  LOAD_MORE,
  PREV_PAGE,
  SELECTED_PRODUCT_ID,
  SELECTED_PRODUCT_PAGE,
} from '@constants/pagination';
import useGeneralSettings from '@hooks/useGeneralSettings';
import { useAdditionalElementState } from '@providers/AdditionalElementProvider';
import { useValidateBreakpoint } from '@providers/AppProvider';
import { useFilterSettings, useFilterState } from '@providers/FilterProvider';
import { useProductListState } from '@providers/ProductListProvider';
import { useAdditionalElementThemeSettings } from '@providers/ThemeProvider/Provider/AdditionalElementThemeSettings';
import { useProductItemThemeSettings } from '@providers/ThemeProvider/Provider/ProductItemThemeSettings';
import { getSessionStorage, setSessionStorage } from '@utils';
import classnames from '@utils/classnames';
import { useEffect, useImperativeHandle, useMemo, useRef } from 'react';

import ProductListPlaceholder from '@/features/filter-product-list/product-list/list/ProductListPlaceholder';
import { ProductItemListViewLayout } from '@/features/filter-product-list/product-list/product-item/ProductItem';

import ProductItem from '../ProductItem';
import { useConnectProductImg } from '../ProductItem/connectors/connectProductImg';
import { useConnectProductInfo } from '../ProductItem/connectors/connectProductInfo';
import { useConnectViewAsOption } from './connectors/connectViewAsOptionState';
import type { ProductListPaginationProps } from './Pagination';
import ProductListPagination from './Pagination';
import useKeepPositionOnBack from './useKeepPositionOnBack';

export type ProductListProps = RegistryComponentProps<
  {
    className?: string;
    pagination?: {
      enable: boolean;
    };
    scrollToTopContainerOnLoadProducts?: {
      enable: boolean;
      behavior?: ScrollBehavior;
      target?: 'product-list' | 'window' | HTMLElement;
    };
    callbackFuncAppIntegration?: (() => void)[];
    enableKeepScrollbackPosition?: boolean;
    scrollBackOffset?: number;
    scrollBackAwait?: number;
    afterRender?: () => void;
    disablePartialRender?: boolean;
  },
  {
    getProductHandles?: () => string[];
  }
>;

const ProductList = ({
  className,
  pagination = {
    enable: true,
  },
  scrollToTopContainerOnLoadProducts = { enable: true, behavior: 'smooth' },
  callbackFuncAppIntegration,
  enableKeepScrollbackPosition = true,
  scrollBackOffset,
  scrollBackAwait,
  helpersRef,
  afterRender,
  disablePartialRender = false,
}: ProductListProps) => {
  const productListElementRef = useRef<HTMLDivElement | null>(null);

  const { props: viewAsOptionProps } = useConnectViewAsOption();

  const {
    products,
    totalProducts,
    pagination: paginationState,
    changeProductPage,
    currentCollectionSelected,
  } = useProductListState();

  const { loadingAdditional, viewAsGridMultiColListOption } = useAdditionalElementState();

  useImperativeHandle(helpersRef, () => ({
    getProductHandles() {
      const getCurrentProductIds = products
        .slice(-(paginationState?.limit || 24))
        .map((item) => item.handle);

      return getCurrentProductIds;
    },
    getProductListHtmlByAjax,
    setAjaxLoading,
    performScrollRestoration,
  }));

  const {
    sharedAdditionalElementData: { limit },
    setAjaxLoading,
  } = useFilterState();

  const mobileBreakpoint = useValidateBreakpoint('mobile');

  const {
    pagination: { paginationType },
  } = useAdditionalElementThemeSettings();

  const {
    productImg: { elements: productImgElementsSettings },
    general: productItemGeneralSettings,
  } = useProductItemThemeSettings();

  const hideOtherLabelsWhenSoldOut =
    productImgElementsSettings?.productSoldOutLabel?.hideOtherLabelsWhenSoldOut;

  const { borderLayout } = productItemGeneralSettings;

  const { showVariantImageBasedOnSelectedFilter, isShortenUrlParam, urlScheme, filterPrefixParam } =
    useFilterSettings();

  const {
    generalSettings: {
      no_image_url,
      current_tags,
      addCollectionToProductUrl: hasCollection,
      priceSettings,
    },
  } = useGeneralSettings();

  // scroll restoration
  const { performScrollRestoration } = useKeepPositionOnBack(products, {
    enableKeepScrollbackPosition,
    scrollBackOffset,
    scrollBackAwait,
  });

  const pageRef = useRef<number>(paginationState?.page || 1);

  const { props: productInfoProps } = useConnectProductInfo();
  const { props: productImgProps } = useConnectProductImg();

  const trackPosition = (index: number, productId: number) => {
    const pageParam = getQueryParamByKey('page');
    const currPage = pageParam ? Number(pageParam) : 1;
    const page = Math.ceil((index + 1) / limit) || 1;
    const prevPage = getSessionStorage(PREV_PAGE);
    const calc = {
      [DEFAULT]: currPage,
      [LOAD_MORE]: prevPage - 1 + page,
      [INFINITE]: prevPage - 1 + page,
      [CURRENT_PER_TOTAL]: currPage,
    };

    if (!paginationType) return;

    const actualPage = calc[paginationType];

    setSessionStorage(SELECTED_PRODUCT_PAGE, calc[paginationType]);
    setSessionStorage(SELECTED_PRODUCT_ID, productId);

    setQueryParam('page', actualPage.toString(), true);
  };

  const productListPaginationProps: ProductListPaginationProps = {
    products,
    totalProducts,
    productListElementRef,
    scrollToTopContainerOnLoadProducts,
    paginationState,
    changeProductPage,
    enable: pagination.enable,
    currentCollectionSelected,
  };

  const getColClassName = () => {
    return `product-list-${viewAsOptionProps.viewAsOption}-col`;
  };

  const disabledFromCustomization = disablePartialRender;
  const hasScrollBack = enableKeepScrollbackPosition && !!getSessionStorage(SELECTED_PRODUCT_ID);
  const pageChanged = pageRef.current !== paginationState?.page;

  /**
   * Disable partial render when:
   * 1. Customization
   * 2. Scroll back: Since product list need to be fully rendered from the beginning to scroll down to the selected product
   * 3. Not initial page: Only apply partial rendering the first initial page, the rest will be rendered normally to avoid flickering issue when loading with Load more/Infinite scroll
   * For example: Access page 2 => apply partial render => go to page 3 => apply normal render
   */
  const disabledPartialRender = disabledFromCustomization || hasScrollBack || pageChanged;

  const autoAspectRatio = useMemo(() => {
    return products.reduce((maxRatio, currentProduct) => {
      const firstImage = currentProduct.images_info.length > 0 && currentProduct.images_info[0];
      if (!firstImage) return maxRatio;

      const { height, width } = firstImage;

      return Number(Math.max(height / width, maxRatio).toFixed(2));
    }, 0);
  }, [products]);

  useEffect(() => {
    if (callbackFuncAppIntegration) {
      callbackFuncAppIntegration.forEach((callback: () => void) => callback?.());
    }
  }, [viewAsOptionProps.viewAsOption]);

  const firstLoadRef = useRef<boolean>(true);

  if (!loadingAdditional && firstLoadRef.current) {
    firstLoadRef.current = false;
  }

  useEffect(() => {
    if (!firstLoadRef.current) {
      performScrollRestoration();
    }
  }, [firstLoadRef.current]);

  if (firstLoadRef.current) {
    /**
     * Render corresponding placeholder with product list settings
     * @example viewAsGridMultiColListOption: 'list' | 'grid--1' | 'grid--2' | ...
     */
    const productListSettings =
      viewAsGridMultiColListOption === 'list'
        ? { productListLayout: 'list' as const, columns: 1 }
        : {
            productListLayout: 'grid' as const,
            columns: Number(viewAsGridMultiColListOption.split('--')[1]),
          };

    return <ProductListPlaceholder {...productListSettings} />;
  }

  return (
    <ProductListPagination {...productListPaginationProps}>
      <div
        ref={productListElementRef}
        className={classnames(
          'product-list',
          className,
          getColClassName(),
          borderLayout === 'noBorder' && 'product-list--margin',
          borderLayout === 'hasPadding' && 'product-list--margin',
          borderLayout === 'noPadding' && 'product-list--border-no-margin',
          borderLayout === 'noPaddingImage' && 'product-list--border-no-margin'
        )}
      >
        {products.length > 0 && (
          <PartialRender
            items={products}
            elementRef={productListElementRef}
            method={mobileBreakpoint ? 'touch-window' : 'delay'}
            partSize={mobileBreakpoint ? 4 : 8}
            disabled={disabledPartialRender}
            onItemRender={(product, index) => (
              <ProductItem
                autoAspectRatio={`1/${autoAspectRatio}`}
                key={`${product.id} ${product?.variant_id || ''}`}
                product={product}
                productInfoSettings={productInfoProps}
                productImgSettings={productImgProps}
                hasCollection={hasCollection}
                priceSettings={priceSettings}
                current_tags={current_tags}
                showVariantImageBasedOnSelectedFilter={showVariantImageBasedOnSelectedFilter}
                isShortenUrlParam={isShortenUrlParam}
                urlScheme={urlScheme}
                filterPrefixParam={filterPrefixParam}
                no_image_url={no_image_url}
                hideOtherLabelsWhenSoldOut={hideOtherLabelsWhenSoldOut}
                productItemGeneralSettings={productItemGeneralSettings}
                onClick={() => trackPosition(index, product.id)}
                onRender={
                  viewAsOptionProps.viewAsOption === 'list'
                    ? function (productItemProps) {
                        return <ProductItemListViewLayout {...productItemProps} />;
                      }
                    : undefined
                }
              />
            )}
          />
        )}
      </div>
    </ProductListPagination>
  );
};

export default registryComponent('ProductList', ProductList);
